import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ============================================
// Enhanced Datetime Utilities with Timezone Support
// ============================================

/**
 * Convert a UTC datetime string/Date to local datetime-local input format
 * This properly handles timezone conversion from UTC to user's local timezone
 */
export function formatDateTimeLocal(date: Date | string | null | undefined): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  // Format for datetime-local input (YYYY-MM-DDTHH:MM)
  // This automatically converts UTC to user's local timezone
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Convert date-only input value (YYYY-MM-DD) to UTC ISO string.
 * Uses end-of-day in local timezone for expiration-style dates.
 */
export function convertDateLocalToISO(value: string): string {
  if (!value) return '';

  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d, 23, 59, 59);

  if (isNaN(date.getTime())) {
    throw new Error('Invalid date value');
  }

  return date.toISOString();
}

/**
 * Convert datetime-local input value to UTC ISO string
 * This properly handles timezone conversion from user's local timezone to UTC
 */
export function convertDateTimeLocalToISO(value: string): string {
  if (!value) return '';

  // Create date from datetime-local value (treats as local timezone)
  const date = new Date(value);

  if (isNaN(date.getTime())) {
    throw new Error('Invalid datetime value');
  }

  // Convert to UTC ISO string
  return date.toISOString();
}

/**
 * Parse UTC ISO string to Date object
 * Handles both 'Z' suffix and '+00:00' timezone formats
 */
export function parseISOToDate(isoString: string): Date {
  if (!isoString) throw new Error('ISO string is required');

  // Handle 'Z' suffix by replacing with '+00:00'
  const normalizedISO = isoString.replace('Z', '+00:00');
  const date = new Date(normalizedISO);

  if (isNaN(date.getTime())) {
    throw new Error('Invalid ISO datetime string');
  }

  return date;
}

/**
 * Format UTC datetime for display in user's local timezone
 * Options for different display formats
 */
export function formatDisplayDateTime(
  date: Date | string | null | undefined,
  options: {
    dateStyle?: 'full' | 'long' | 'medium' | 'short';
    timeStyle?: 'full' | 'long' | 'medium' | 'short';
    includeTime?: boolean;
    locale?: string;
  } = {}
): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? parseISOToDate(date) : date;

  const { dateStyle = 'medium', timeStyle = 'short', includeTime = true, locale = 'de-DE' } = options;

  if (includeTime) {
    return dateObj.toLocaleString(locale, {
      dateStyle,
      timeStyle,
    });
  } else {
    return dateObj.toLocaleDateString(locale, {
      dateStyle,
    });
  }
}

/**
 * Format datetime for short display (e.g., in cards, lists)
 */
export function formatShortDateTime(date: Date | string | null | undefined): string {
  return formatDisplayDateTime(date, {
    dateStyle: 'short',
    timeStyle: 'short',
    locale: 'de-DE',
  });
}

/**
 * Format date only (no time) for display
 */
export function formatDisplayDate(date: Date | string | null | undefined): string {
  return formatDisplayDateTime(date, {
    dateStyle: 'medium',
    includeTime: false,
    locale: 'de-DE',
  });
}

/**
 * Check if a datetime is in the past (timezone-aware)
 */
export function isDateInPast(date: Date | string): boolean {
  const dateObj = typeof date === 'string' ? parseISOToDate(date) : date;
  return dateObj < new Date();
}

/**
 * Check if a datetime is in the future (timezone-aware)
 */
export function isDateInFuture(date: Date | string): boolean {
  const dateObj = typeof date === 'string' ? parseISOToDate(date) : date;
  return dateObj > new Date();
}

/**
 * Check if current date/time is between two dates (timezone-aware)
 */
export function isDateBetween(startDate: Date | string, endDate: Date | string): boolean {
  const now = new Date();
  const start = typeof startDate === 'string' ? parseISOToDate(startDate) : startDate;
  const end = typeof endDate === 'string' ? parseISOToDate(endDate) : endDate;

  return now >= start && now <= end;
}

/**
 * Validate that end date is after start date
 */
export function validateDateRange(startDate: string | Date, endDate: string | Date): boolean {
  const start = typeof startDate === 'string' ? parseISOToDate(startDate) : startDate;
  const end = typeof endDate === 'string' ? parseISOToDate(endDate) : endDate;

  return end > start;
}

/**
 * Get relative time description (e.g., "2 days ago", "in 3 hours")
 */
export function getRelativeTime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? parseISOToDate(date) : date;
  const now = new Date();
  const diffMs = dateObj.getTime() - now.getTime();
  const rtf = new Intl.RelativeTimeFormat('de-DE', { numeric: 'auto' });

  const units = [
    { unit: 'year' as const, ms: 1000 * 60 * 60 * 24 * 365 },
    { unit: 'month' as const, ms: 1000 * 60 * 60 * 24 * 30 },
    { unit: 'day' as const, ms: 1000 * 60 * 60 * 24 },
    { unit: 'hour' as const, ms: 1000 * 60 * 60 },
    { unit: 'minute' as const, ms: 1000 * 60 },
  ];

  for (const { unit, ms } of units) {
    const value = Math.round(diffMs / ms);
    if (Math.abs(value) >= 1) {
      return rtf.format(value, unit);
    }
  }

  return rtf.format(0, 'second');
}
